// #25 automatic payments, phase 3: the pure logic behind the Set up automatic payments card, the collect-signature summary
// of an APPROVE_ALLOWANCE and the My Account entry. Every check here mirrors ns-lambda-ts `_core/public-ledger/
// auto-settings.ts`, which decides: the card checks first so a person sees each problem beside its field, and the propose
// lambda checks again at today's price.
//
// Units (DECISION 1.6, 1.6b): the company threshold is in credits and its target in pence; the operating threshold and target
// are in credits; both monthly limits are in pence. A rate is pence per credit, kept to a millionth of a penny.
import { formatCredits } from './public-ledger-credits.js';
import { formatPence, formatUnitPence } from './credit-pricing.js';
import { cardBrandName, cardExpiry } from './payment-card.js';
import { formatDate } from './format.js';
import { COUNTRY_NAMES, normaliseBillingDetails, type BillingDetails } from './billing-details.js';

// The mandate text the card shows beside the card form; confirm-setup (ns-lambda-ts autopay/confirm-setup.ts
// MANDATE_TEXT_VERSIONS) refuses any other version, so a change to the text bumps both
export const MANDATE_TEXT_VERSION = '2026-11-02';

// The wording needs a legal check before automatic payments go live. A sentence saying how to stop automatic payments
// joins it (with a new version) once signatories can stop them (.ai/backlog.md)
export const MANDATE_TEXT =
  'By saving this card you authorise nodeStream to charge it while you are not present, for your company’s automatic ' +
  'payments for public ledger actions: each time your company’s public ledger account falls below the threshold the ' +
  'signatories set, the card is charged the target they set, never more in a calendar month than the monthly card limit ' +
  'they set. Nothing is charged until the signatories have signed those amounts, and changed amounts apply only once ' +
  'the signatories have signed them.';

// #40 (DECISION 2): shown beside every card form for automatic payments. The card is the company's: only a signed change of
// card or a signed stop takes it off, so a personal card would stay in use after its owner left. Wording not yet reviewed
export const COMPANY_CARD_NOTICE =
  'Use a company card, never a personal one. The card stays in use, even if you leave the company, until the ' +
  'signatories sign a change of card or a stop of automatic payments.';

// The note shown wherever an amount is converted at today's price
export const RATE_NOTE =
  'Conversions use today’s price per credit, which changes. Thresholds in credits stay as set; the company target stays ' +
  'in pounds, so it buys more or fewer credits as the price moves.';

// The note under the settings a signatory signs: the conversions shown are at the price when they were proposed
export const APPROVE_RATE_NOTE =
  'Pounds and credits are converted at the price when the settings were proposed. The price changes: the thresholds ' +
  'stay in credits and the company target stays in pounds.';

export const AUTOPAY_CODES = {
  notSignatory: 'PUBLIC_LEDGER_NOT_SIGNATORY',
  noAccount: 'PUBLIC_LEDGER_AUTOPAY_NO_ACCOUNT',
  alreadySetUp: 'PUBLIC_LEDGER_AUTOPAY_ALREADY_SET_UP',
  changeOpen: 'PUBLIC_LEDGER_AUTOPAY_CHANGE_OPEN',
  signatoryChange: 'PUBLIC_LEDGER_SIGNATORY_CHANGE_PENDING',
  noCard: 'PUBLIC_LEDGER_AUTOPAY_NO_CARD',
  cardNotConfirmed: 'PUBLIC_LEDGER_AUTOPAY_CARD_NOT_CONFIRMED',
  noRate: 'PUBLIC_LEDGER_AUTOPAY_NO_RATE',
  notCardOwner: 'PUBLIC_LEDGER_AUTOPAY_NOT_CARD_OWNER',
  settingsInvalid: 'PUBLIC_LEDGER_AUTOPAY_SETTINGS_INVALID',
  signAgain: 'PUBLIC_LEDGER_AUTOPAY_SIGN_AGAIN',
  beingConfirmed: 'PUBLIC_LEDGER_AUTOPAY_SIGNATURE_BEING_CONFIRMED',
  underReview: 'PUBLIC_LEDGER_AUTOPAY_UNDER_REVIEW',
  // #25 phase 6: a change needs automatic payments on
  notActive: 'PUBLIC_LEDGER_AUTOPAY_NOT_ACTIVE',
  // #25 phase 6 (round 10, the round 9 follow-up): the last signature of a change of amounts waits for its fee in the
  // operating account, however it is coming (the company's own top-up or card charge, nodeStream's free top-up, one staff
  // paid for past the monthly limit, or one the public mirror doesn't show yet); the signing card stops at once and shows
  // FEE_WAITING_FOR_TOP_UP, never a spinner or an error
  feeTopUpWaiting: 'PUBLIC_LEDGER_AUTOPAY_FEE_TOP_UP_WAITING',
} as const;

// What the last signer's card shows while the fee waits for the operating account's top-up (approved, round 5 and round 8
// follow-ups; round 9 follow-up: for every such wait); they are told when they can sign ("You can sign the change now"),
// and sign with one press
export const FEE_WAITING_FOR_TOP_UP =
  "This change is waiting for your company's operating account to be topped up. We'll let you know when you can sign.";

const MICRO = 1000000;

export interface AutoAmounts {
  companyThresholdCredits: number;
  companyTargetPence: number;
  operatingThresholdCredits: number;
  operatingTargetCredits: number;
  monthlyCardLimitPence: number;
  monthlyTopUpLimitPence: number;
  // null: no "change target" card when prices move; else a whole 1-100
  targetAlertPercent: number | null;
}

export type AmountField = keyof AutoAmounts;

export const AMOUNT_FIELDS: readonly AmountField[] = [
  'companyThresholdCredits',
  'companyTargetPence',
  'operatingThresholdCredits',
  'operatingTargetCredits',
  'monthlyCardLimitPence',
  'monthlyTopUpLimitPence',
  'targetAlertPercent',
];

export interface SettingsProblem {
  field: AmountField;
  reason: string;
}

// public-ledger/autopay/figures' answer
export interface SetupFigures {
  // Today's price per credit in pence; null when no rate can be read (set-up is then refused)
  unitPricePence: number | null;
  rateAt: string | null;
  minCredits: number;
  signaturesNeeded: number;
  reserveCredits: number;
  // The company threshold's floor (#41): £10 (the environment store's credits.minCompanyThresholdPence) and what it buys at
  // today's price, null with no rate
  minCompanyThresholdPence: number;
  minCompanyThresholdCredits: number | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const isWhole = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

// The figures answer, or null when it isn't one
export function parseFigures(value: unknown): SetupFigures | null {
  if (!isRecord(value)) return null;
  const {
    unitPricePence,
    rateAt,
    minCredits,
    signaturesNeeded,
    reserveCredits,
    minCompanyThresholdPence,
    minCompanyThresholdCredits,
  } = value;
  const price = typeof unitPricePence === 'number' && Number.isFinite(unitPricePence) && unitPricePence > 0;
  if (!(price || unitPricePence === null)) return null;
  if (!isWhole(minCredits) || !isWhole(signaturesNeeded) || !isWhole(reserveCredits)) return null;
  if (
    !isWhole(minCompanyThresholdPence) ||
    !(isWhole(minCompanyThresholdCredits) || minCompanyThresholdCredits === null)
  )
    return null;
  return {
    unitPricePence: price ? (unitPricePence as number) : null,
    rateAt: typeof rateAt === 'string' ? rateAt : null,
    minCredits,
    signaturesNeeded,
    reserveCredits,
    minCompanyThresholdPence,
    minCompanyThresholdCredits,
  };
}

// The whole credits pence buys at a rate (rounded down), or 0 for no rate: as the server's creditsForPence
export function creditsForPence(pence: number, unitPricePence: number): number {
  const microPrice = Math.round(unitPricePence * MICRO);
  if (!(microPrice > 0) || !(pence > 0) || !Number.isFinite(pence)) return 0;
  return Number((BigInt(Math.floor(pence)) * BigInt(MICRO)) / BigInt(microPrice));
}

// What whole credits cost at a rate in pence, rounded up: as the server's totalPence
export function penceForCredits(credits: number, unitPricePence: number): number {
  if (!Number.isSafeInteger(credits) || credits < 0 || !Number.isFinite(unitPricePence)) return 0;
  const micro = BigInt(Math.round(unitPricePence * MICRO));
  const m = BigInt(MICRO);
  return Number((BigInt(credits) * micro + m - BigInt(1)) / m);
}

// Pence as pounds for the company threshold's floor: £10 for whole pounds, £0.50 and £10.25 otherwise. As the server's
// auto-settings formatFloorPounds, so the reasons read the same
export function formatFloorPounds(pence: number): string {
  const safe = Math.max(0, Math.floor(pence));
  const whole = new Intl.NumberFormat('en-GB').format(Math.floor(safe / 100));
  const rest = safe % 100;
  return rest === 0 ? `£${whole}` : `£${whole}.${String(rest).padStart(2, '0')}`;
}

// The company Threshold field's hint (#41): "At least £10, about 2,873 credits at today's price.", or null with no price
export function companyThresholdFloorText(figures: SetupFigures | null | undefined): string | null {
  if (!figures || figures.minCompanyThresholdCredits === null) return null;
  return `At least ${formatFloorPounds(figures.minCompanyThresholdPence)}, about ${formatCredits(figures.minCompanyThresholdCredits)} credits at today's price.`;
}

/**
 * The set-up checks (DECISION 2.5, 1.7, 3.7; #41's company threshold floor), the same rules and wording as the server's
 * validateSettings, each against the field to change. They need today's price; without one nothing can be checked (the
 * server refuses with NO_RATE).
 */
export function validateSettings(
  amounts: AutoAmounts,
  context: { unitPricePence: number; minCredits: number; reserveCredits: number; minCompanyThresholdPence: number },
): SettingsProblem[] {
  const problems: SettingsProblem[] = [];
  const { unitPricePence, minCredits, reserveCredits, minCompanyThresholdPence } = context;
  const targetCredits = creditsForPence(amounts.companyTargetPence, unitPricePence);
  const thresholdFloor = creditsForPence(minCompanyThresholdPence, unitPricePence);
  if (amounts.companyThresholdCredits < thresholdFloor) {
    problems.push({
      field: 'companyThresholdCredits',
      reason: `The company threshold must be at least ${formatCredits(thresholdFloor)} credits, what ${formatFloorPounds(minCompanyThresholdPence)} buys at today's price`,
    });
  }
  if (targetCredits < amounts.companyThresholdCredits + minCredits) {
    problems.push({
      field: 'companyTargetPence',
      reason: `The target must buy at least ${formatCredits(minCredits)} credits more than the threshold at today's price`,
    });
  }
  if (amounts.operatingThresholdCredits < reserveCredits) {
    problems.push({
      field: 'operatingThresholdCredits',
      reason: `The operating threshold must be at least ${formatCredits(reserveCredits)} credits, the reserve kept for fees`,
    });
  }
  if (amounts.operatingTargetCredits <= amounts.operatingThresholdCredits) {
    problems.push({
      field: 'operatingTargetCredits',
      reason: 'The operating target must be more than the operating threshold',
    });
  } else if (amounts.operatingTargetCredits > targetCredits) {
    problems.push({
      field: 'operatingTargetCredits',
      reason: "The operating target must be no more than the company target buys at today's price",
    });
  }
  if (amounts.monthlyCardLimitPence < amounts.companyTargetPence) {
    problems.push({
      field: 'monthlyCardLimitPence',
      reason: 'The monthly card limit must be at least the company target',
    });
  }
  if (amounts.monthlyTopUpLimitPence < penceForCredits(amounts.operatingTargetCredits, unitPricePence)) {
    problems.push({
      field: 'monthlyTopUpLimitPence',
      reason: "The monthly top-up limit must cover at least one top-up to the operating target at today's price",
    });
  }
  return problems;
}

/**
 * The problems in a PUBLIC_LEDGER_AUTOPAY_SETTINGS_INVALID message (`field: reason; field: reason`), by field. A part that
 * names no known field is left out, so the caller shows the whole message when nothing is found.
 */
export function settingsProblemsFromMessage(message: string): Partial<Record<AmountField, string>> {
  const found: Partial<Record<AmountField, string>> = {};
  for (const part of message.split(';')) {
    const at = part.indexOf(':');
    if (at < 0) continue;
    const field = part.slice(0, at).trim() as AmountField;
    const reason = part.slice(at + 1).trim();
    if (AMOUNT_FIELDS.includes(field) && reason && !found[field]) found[field] = reason;
  }
  return found;
}

/**
 * A pound amount typed by a person, in whole pence: digits with grouping commas or spaces, an optional leading £ and at
 * most two decimals. Null for anything else (a third decimal is refused, not rounded) or above the safe range.
 */
export function parsePoundsToPence(input: string | null | undefined): number | null {
  const text = (input ?? '').replace(/[,\s]/g, '').replace(/^£/, '');
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const pounds = Number(match[1]);
  const pence = pounds * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(pence) ? pence : null;
}

/** A whole number typed by a person (grouping commas or spaces allowed), or null: credits and the percentage. */
export function parseWhole(input: string | null | undefined): number | null {
  const text = (input ?? '').replace(/[,\s]/g, '');
  if (!/^\d+$/.test(text)) return null;
  const n = Number(text);
  return Number.isSafeInteger(n) ? n : null;
}

// What the card's form holds: the text of each amount and whether the price alert is on
export interface SettingsDraft {
  companyThresholdCredits: string;
  companyTarget: string;
  operatingThresholdCredits: string;
  operatingTargetCredits: string;
  monthlyCardLimit: string;
  monthlyTopUpLimit: string;
  alertOn: boolean | null;
  alertPercent: string;
}

export const EMPTY_DRAFT: SettingsDraft = {
  companyThresholdCredits: '',
  companyTarget: '',
  operatingThresholdCredits: '',
  operatingTargetCredits: '',
  monthlyCardLimit: '',
  monthlyTopUpLimit: '',
  alertOn: null,
  alertPercent: '',
};

/**
 * The amounts from the form, or the format problems by field (an empty or unreadable amount, a percentage outside 1-100,
 * or the price alert question not answered). The rules between amounts are validateSettings'.
 */
export function amountsFromDraft(
  draft: SettingsDraft,
): { ok: true; amounts: AutoAmounts } | { ok: false; problems: SettingsProblem[] } {
  const problems: SettingsProblem[] = [];
  const credits = (text: string, field: AmountField) => {
    const n = parseWhole(text);
    if (n === null) problems.push({ field, reason: 'Enter a whole number of credits' });
    return n ?? 0;
  };
  const pence = (text: string, field: AmountField) => {
    const n = parsePoundsToPence(text);
    if (n === null) problems.push({ field, reason: 'Enter an amount in pounds, such as 250 or 250.50' });
    return n ?? 0;
  };
  const amounts: AutoAmounts = {
    companyThresholdCredits: credits(draft.companyThresholdCredits, 'companyThresholdCredits'),
    companyTargetPence: pence(draft.companyTarget, 'companyTargetPence'),
    operatingThresholdCredits: credits(draft.operatingThresholdCredits, 'operatingThresholdCredits'),
    operatingTargetCredits: credits(draft.operatingTargetCredits, 'operatingTargetCredits'),
    monthlyCardLimitPence: pence(draft.monthlyCardLimit, 'monthlyCardLimitPence'),
    monthlyTopUpLimitPence: pence(draft.monthlyTopUpLimit, 'monthlyTopUpLimitPence'),
    targetAlertPercent: null,
  };
  if (draft.alertOn === null) {
    problems.push({ field: 'targetAlertPercent', reason: 'Choose whether to be told when prices move' });
  } else if (draft.alertOn) {
    const percent = parseWhole(draft.alertPercent);
    if (percent === null || percent < 1 || percent > 100) {
      problems.push({ field: 'targetAlertPercent', reason: 'Enter a whole percentage from 1 to 100' });
    } else {
      amounts.targetAlertPercent = percent;
    }
  }
  return problems.length > 0 ? { ok: false, problems } : { ok: true, amounts };
}

// "worth about £12.34 at today's price" for credits, or null with no price or no amount
export function creditsWorthText(credits: number | null, unitPricePence: number | null): string | null {
  if (credits === null || unitPricePence === null || !(unitPricePence > 0)) return null;
  return `Worth about ${formatPence(penceForCredits(credits, unitPricePence))} at today's price`;
}

// "buys 12,345 credits at today's price" for pence, or null with no price or no amount
export function penceBuysText(pence: number | null, unitPricePence: number | null): string | null {
  if (pence === null || unitPricePence === null || !(unitPricePence > 0)) return null;
  return `Buys ${formatCredits(creditsForPence(pence, unitPricePence))} credits at today's price`;
}

// The plAutoPayment row as a signatory reads it (the policy's projection)
export interface AutoPaymentCard {
  cardOwnerId?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  cardExpMonth?: number | null;
  cardExpYear?: number | null;
}

export interface ProposedSettings extends Partial<AutoAmounts> {
  settingsVersion?: number | null;
  unitPricePence?: number | null;
  companyTargetCreditsAtSigning?: number | null;
  card?: AutoPaymentCard | null;
  // #41 part 5: the billing details a SETUP proposal carries (signed with the settings); absent on every other kind
  billing?: BillingDetails | null;
}

export interface AutoPaymentRow extends AutoPaymentCard, Partial<AutoAmounts> {
  id?: string;
  companyId?: string;
  setupState?: 'NONE' | 'SETTING_UP' | 'ACTIVE' | string | null;
  // What the target bought when the live settings were signed (the price alert's basis)
  companyTargetCreditsAtSigning?: number | null;
  // The health fields (#25 phase 4, 04's transition table)
  cardHealth?: 'OK' | 'FAILED' | 'AUTH_REQUIRED' | string | null;
  allowanceHealth?: 'OK' | 'MISSING' | string | null;
  targetHealth?: 'OK' | 'BELOW_PERCENT' | 'STOPPED' | string | null;
  // Set by liquidity-check while a job waits for the operating target to be raised (round 4: ABOVE_OPERATING_TARGET, kept
  // after a Raise card is cancelled), until the target covers it or the job settles
  operatingTargetWaiting?: boolean | null;
  // The UK month ('YYYY-MM') each kind of payment is stopped for, once its monthly limit is reached
  monthStops?: { card?: string | null; operating?: string | null } | null;
  settingsVersion?: number | null;
  // The live allowance's approval: its schedule on the public ledger (the browser's re-sign anchor reads it there)
  allowance?: { approvedAt?: string | null; scheduleId?: string | null } | null;
  // #41 part 5: the automatic payments Stripe customer's billing details, written when the set-up is applied
  billing?: BillingDetails | null;
  pendingChange?: {
    kind?: string | null;
    transactionId?: string | null;
    requestedBy?: string | null;
    createdAt?: string | null;
    // #40: a stop's signatures met the rule; apply settles it next
    signedAt?: string | null;
    proposed?: ProposedSettings | null;
  } | null;
  // #40: when the signatories' stop ran on the public ledger, and who asked for it
  stoppedAt?: string | null;
  stoppedBy?: string | null;
}

export type SetupStage = 'ALREADY_SET_UP' | 'CHANGE_OPEN' | 'CARD' | 'AMOUNTS' | 'PROPOSED';

/**
 * Where the set-up card is, from the company's row and who holds the card:
 * - ALREADY_SET_UP: automatic payments are on, so there is nothing to set up;
 * - PROPOSED: this set-up's proposal is recorded (propose answered); the card is completed with `proposed`;
 * - CHANGE_OPEN: settings someone else proposed are waiting for signatures;
 * - AMOUNTS: the holder saved a card on this set-up and chooses the amounts;
 * - CARD: a card is still to be saved by the holder (none yet, or one another signatory saved, which only they can propose).
 */
export function setupStage(
  row: AutoPaymentRow | null | undefined,
  personId: string | null | undefined,
  proposedTransactionId: string | null = null,
): SetupStage {
  if (row?.setupState === 'ACTIVE') return 'ALREADY_SET_UP';
  const pending = row?.pendingChange ?? null;
  const transactionId = pending?.transactionId ?? null;
  if (transactionId) {
    const mine = transactionId === proposedTransactionId || (!!personId && pending?.requestedBy === personId);
    return mine ? 'PROPOSED' : 'CHANGE_OPEN';
  }
  const card = pending?.proposed?.card;
  if (card?.cardLast4 && personId && card.cardOwnerId === personId) return 'AMOUNTS';
  return 'CARD';
}

export type SetupAction = 'CLAIM' | 'CANCEL' | 'ADD_CARD' | 'SAVE_CARD' | 'PROPOSE' | 'COMPLETE';

/**
 * The buttons the set-up card offers, in order, for its stage. Cancel set-up is always there once the card is claimed, the
 * PROPOSED stage included: if completing keeps failing (or the page reloaded before it did), the holder can still end the
 * set-up, whose end runs apply to clear an unscheduled proposal.
 */
export function setupActions(
  stage: SetupStage,
  { claimed, cardFormOpen }: { claimed: boolean; cardFormOpen: boolean },
): SetupAction[] {
  if (!claimed) return ['CLAIM'];
  if (stage === 'PROPOSED') return ['CANCEL', 'COMPLETE'];
  if (stage === 'ALREADY_SET_UP' || stage === 'CHANGE_OPEN') return ['CANCEL'];
  if (cardFormOpen) return ['CANCEL', 'SAVE_CARD'];
  if (stage === 'CARD') return ['CANCEL', 'ADD_CARD'];
  return ['CANCEL', 'PROPOSE'];
}

/**
 * Why the card must not sign what prepare-signature answered, or null to sign: the server named no stored proposal for this
 * approval, the card shows none, or the two differ (the settings changed while the card was open). Nothing is ever signed
 * unseen.
 */
export function approveSignRefusal(
  served: ProposedSettings | null | undefined,
  shown: ProposedSettings | null | undefined,
): string | null {
  if (!served) return 'These settings are no longer waiting for signatures.';
  if (!shown) return 'The settings are still loading. Please try again in a moment.';
  if (!sameSummary(served, shown))
    return 'The settings changed while this card was open. Please check them again before signing.';
  return null;
}

// What a re-sign must leave as it is: every amount and what the target bought at signing. The card isn't signed any more
// (#25 phase 6: the digest covers the amounts only, and a card change is a ledger record of its own); the server checks
// the payment method is unchanged, which the browser can't read
const RESIGN_SAME: readonly (keyof AutoAmounts | 'companyTargetCreditsAtSigning')[] = [
  'companyThresholdCredits',
  'companyTargetPence',
  'companyTargetCreditsAtSigning',
  'targetAlertPercent',
  'operatingThresholdCredits',
  'operatingTargetCredits',
  'monthlyCardLimitPence',
  'monthlyTopUpLimitPence',
];

/**
 * Whether a re-sign's proposal changes the company's live settings (the Re-sign allowance card tells the signatories they
 * are unchanged, #25 phase 4): the first field that differs, or null when it puts them back as they are. A missing live
 * row counts as changed.
 */
export function resignChanges(
  proposed: ProposedSettings | null | undefined,
  live: AutoPaymentRow | null | undefined,
): string | null {
  if (!proposed || !live) return 'settings';
  for (const field of RESIGN_SAME) {
    if ((proposed[field] ?? null) !== (live[field] ?? null)) return field;
  }
  return null;
}

export const RESIGN_CHANGED_REFUSAL =
  "These aren't your company's current settings, so they can't be signed to put the allowance back. Please contact nodeStream support.";

// "Visa ending 4242, expires 04/2030"
// "Card" for a card with no usable brand
export function cardText(card: AutoPaymentCard | null | undefined): string | null {
  if (!card?.cardLast4) return null;
  const expiry = cardExpiry(card);
  return `${cardBrandName(card.cardBrand) ?? 'Card'} ending ${card.cardLast4}${expiry ? `, expires ${expiry}` : ''}`;
}

// "Visa •••• 4242": a card as the change of card's lines name it (no expiry)
export function cardDots(card: AutoPaymentCard | null | undefined): string | null {
  if (!card?.cardLast4) return null;
  return `${cardBrandName(card.cardBrand) ?? 'Card'} •••• ${card.cardLast4}`;
}

export interface SummaryRow {
  label: string;
  value: string;
}

/**
 * The settings a signatory signs, from the server's stored proposal (never from values typed in a browser), each amount in
 * its own unit with its conversion at the price it was proposed at. Null when the proposal is missing any amount. The card
 * isn't listed: it isn't signed (#25 phase 6, the digest covers the amounts only). A set-up's billing details are signed
 * with the amounts (#41 part 5), so they're listed after them.
 */
export function approveSummaryRows(proposed: ProposedSettings | null | undefined): SummaryRow[] | null {
  if (!proposed) return null;
  const fields = [
    proposed.companyThresholdCredits,
    proposed.companyTargetPence,
    proposed.operatingThresholdCredits,
    proposed.operatingTargetCredits,
    proposed.monthlyCardLimitPence,
    proposed.monthlyTopUpLimitPence,
  ];
  if (!fields.every(isWhole)) return null;
  const unit =
    typeof proposed.unitPricePence === 'number' && proposed.unitPricePence > 0 ? proposed.unitPricePence : null;
  const inPounds = (credits: number) => (unit ? ` (about ${formatPence(penceForCredits(credits, unit))})` : '');
  const inCredits = (pence: number) => (unit ? ` (buys ${formatCredits(creditsForPence(pence, unit))} credits)` : '');
  const p = proposed as Required<Pick<ProposedSettings, keyof AutoAmounts>> & ProposedSettings;
  const alert = proposed.targetAlertPercent;
  const rows: SummaryRow[] = [
    {
      label: 'Company account threshold',
      value: `${formatCredits(p.companyThresholdCredits as number)} credits${inPounds(p.companyThresholdCredits as number)}`,
    },
    {
      label: 'Company account target, charged to the card',
      value: `${formatPence(p.companyTargetPence as number)}${inCredits(p.companyTargetPence as number)}`,
    },
    {
      label: 'Operating account threshold',
      value: `${formatCredits(p.operatingThresholdCredits as number)} credits${inPounds(p.operatingThresholdCredits as number)}`,
    },
    {
      label: 'Operating account target',
      value: `${formatCredits(p.operatingTargetCredits as number)} credits${inPounds(p.operatingTargetCredits as number)}`,
    },
    { label: 'Monthly card limit', value: formatPence(p.monthlyCardLimitPence as number) },
    { label: 'Monthly operating top-up limit', value: formatPence(p.monthlyTopUpLimitPence as number) },
    {
      label: 'When prices move',
      value:
        typeof alert === 'number'
          ? `Ask the signatories to change the target when it buys less than ${alert}% of the credits it buys now`
          : 'No change target card (charges still stop if the target buys no more than the threshold)',
    },
  ];
  const billing = proposedBilling(proposed);
  if (billing) rows.push(...billingRows(billing));
  // Last, beside the rate note under the list
  if (unit) rows.push({ label: 'Price per credit when proposed', value: formatUnitPence(unit) });
  return rows;
}

// The billing details a proposal carries, normalised as the server signs them, or undefined (never null) when it carries
// none: only a set-up's proposal does, and every other kind reads as an empty block (each leaf null), told apart by its
// missing email
export function proposedBilling(proposed: ProposedSettings | null | undefined): BillingDetails | undefined {
  const billing = proposed?.billing;
  if (!billing || typeof billing !== 'object' || typeof billing.email !== 'string' || !billing.email.trim()) {
    return undefined;
  }
  return normaliseBillingDetails(billing);
}

// "1 High Street, London, EC1A 1AA, United Kingdom"
function billingRows(billing: BillingDetails): SummaryRow[] {
  const { address } = billing;
  const country = COUNTRY_NAMES[address.country] ?? address.country;
  const lines = [address.address1, address.address2, address.city, address.postcode, country].filter(Boolean);
  return [
    { label: 'Billing email', value: billing.email },
    ...(billing.phone ? [{ label: 'Billing phone', value: billing.phone }] : []),
    { label: 'Billing address', value: lines.join(', ') },
  ];
}

// A signature as the submit-signature lambda takes it: lower-case hex
export function signatureHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The SetupIntent Stripe names in the return URL after a redirect (3-D Secure): `setup_intent=seti_…` with
 * `redirect_status`. Null when the URL names none, or a malformed one.
 */
export function setupIntentFromSearch(search: string): { setupIntentId: string; succeeded: boolean } | null {
  const params = new URLSearchParams(search);
  const id = params.get('setup_intent');
  if (!id || !/^seti_[A-Za-z0-9]+$/.test(id)) return null;
  return { setupIntentId: id, succeeded: params.get('redirect_status') === 'succeeded' };
}

/**
 * The My Account "Set up automatic payments" button: for named signatories only, at a company not set up (setupState NONE
 * or no row yet). A pending signatory change disables it with a reason, as propose would refuse.
 */
export function autopaySetupAccess(input: {
  isSignatory: boolean;
  setupState: string | null | undefined;
  changePending: boolean;
}): { visible: boolean; reason: string | null } {
  if (!input.isSignatory) return { visible: false, reason: null };
  const state = input.setupState ?? 'NONE';
  if (state !== 'NONE') return { visible: false, reason: null };
  if (input.changePending) return { visible: true, reason: 'Please wait: the signatory list is being changed' };
  return { visible: true, reason: null };
}

/**
 * #41 (Design 3): the My Account "Buy nodeStream credits" button, for named signatories only, once the company's automatic
 * payments are on (ACTIVE) and no stop the signatories have signed in full is waiting to apply. The server refuses a
 * purchase otherwise (PUBLIC_LEDGER_PURCHASE_AUTOPAY_NEEDED, credits-launch.ts purchaseAutoPaymentsOn); this only hides
 * the button.
 */
export function buyCreditsVisible(input: { isSignatory: boolean; row: AutoPaymentRow | null | undefined }): boolean {
  if (!input.isSignatory || input.row?.setupState !== 'ACTIVE') return false;
  const pending = input.row.pendingChange;
  const stopSigned = pending?.kind === 'STOP' && !!pending.transactionId && !!pending.signedAt;
  return !stopSigned;
}

// The My Account line for the company's automatic payments, from its row (no row yet: not set up). Once they're on, the
// card's owner (`personId`, the viewer's people id, is `cardOwnerId`) reads "your card"; every other signatory reads
// "another signatory's card", with no card details
export function autopayStatusText(row: AutoPaymentRow | null | undefined, personId: string | null | undefined): string {
  const state = row?.setupState ?? 'NONE';
  if (state === 'ACTIVE') {
    // #40: a stop the signatories have signed in full stops every charge and top-up at once
    const stop = row?.pendingChange?.kind === 'STOP' && row.pendingChange.transactionId ? row.pendingChange : null;
    if (stop?.signedAt) return 'Automatic payments are being stopped: the signatories have signed the stop.';
    const own = !!personId && row?.cardOwnerId === personId;
    const on = `Automatic payments are on, charging ${own ? 'your card' : "another signatory's card"}.`;
    return stop ? `${on} A stop is waiting for the signatories to sign it.` : on;
  }
  if (state === 'SETTING_UP') return 'Automatic payments are waiting for the signatories to sign the settings.';
  // #40: stopped by the signatories, until set up again
  const stopped = row?.stoppedAt ? formatDate(row.stoppedAt, 'date') : '';
  if (stopped) return `Automatic payments were stopped on ${stopped}.`;
  return 'Automatic payments are not set up.';
}

/**
 * JSON with every object's keys sorted and no spaces, as ns-lambda-ts canonicalJson: the same settings always give the same
 * text. The browser recomputes the approval's digest with it (approveDigestInput) before it signs.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    const text = JSON.stringify(value);
    return text === undefined ? 'null' : text;
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}

// The approval memo's fixed start, as ns-lambda-ts auto-settings MEMO_PREFIX; the memo is `${prefix}${version} ${first 16
// hex of the digest}`
export const APPROVE_MEMO_PREFIX = 'nodeStream automatic payments v';
const MEMO_DIGEST_CHARS = 16;

/**
 * The settings text the approval's digest is taken over, exactly as ns-lambda-ts auto-settings digestInput writes it: the
 * amounts the signatories read (the user policy's pendingChange.proposed projection), the price they were proposed at,
 * the settings version and the company's id. The card isn't in it (#25 phase 6, decided 2026-10-28): a card change is a
 * ledger record of its own (round 15), so set-up, re-sign and change of amounts sign amounts only. A set-up's billing
 * details (#41 part 5, DECISION 11) go in only when the proposal carries them: the key is left out otherwise, so every
 * other digest stays as it was. Null when a signed field is missing, so nothing is signed that can't be recomputed. Both
 * repos' tests pin the same two vectors, without and with billing.
 */
export function approveDigestInput(
  companyId: string | null | undefined,
  proposed: ProposedSettings | null | undefined,
): string | null {
  const p = proposed;
  const whole = (value: unknown) => typeof value === 'number' && Number.isInteger(value);
  const amounts = [
    p?.companyThresholdCredits,
    p?.companyTargetPence,
    p?.operatingThresholdCredits,
    p?.operatingTargetCredits,
    p?.monthlyCardLimitPence,
    p?.monthlyTopUpLimitPence,
    p?.companyTargetCreditsAtSigning,
  ];
  if (!companyId || !p || !whole(p.settingsVersion) || amounts.some((value) => !whole(value))) return null;
  if (typeof p.unitPricePence !== 'number' || !(p.unitPricePence > 0)) return null;
  return canonicalJson({
    companyId,
    settingsVersion: p.settingsVersion,
    companyThresholdCredits: p.companyThresholdCredits,
    companyTargetPence: p.companyTargetPence,
    operatingThresholdCredits: p.operatingThresholdCredits,
    operatingTargetCredits: p.operatingTargetCredits,
    monthlyCardLimitPence: p.monthlyCardLimitPence,
    monthlyTopUpLimitPence: p.monthlyTopUpLimitPence,
    targetAlertPercent: typeof p.targetAlertPercent === 'number' ? p.targetAlertPercent : null,
    unitPricePence: p.unitPricePence,
    companyTargetCreditsAtSigning: p.companyTargetCreditsAtSigning,
    billing: proposedBilling(p),
  });
}

// The approval's memo for a settings version and the hex sha256 of approveDigestInput
export function approveMemo(settingsVersion: number, digestHex: string): string {
  return `${APPROVE_MEMO_PREFIX}${settingsVersion} ${digestHex.toLowerCase().slice(0, MEMO_DIGEST_CHARS)}`;
}

/**
 * The memo the approval of these settings must carry, recomputed in the browser (WebCrypto sha256), or null when the
 * settings shown can't be recomputed
 */
export async function approveMemoFor(
  companyId: string | null | undefined,
  proposed: ProposedSettings | null | undefined,
): Promise<string | null> {
  const input = approveDigestInput(companyId, proposed);
  if (input === null || !proposed || typeof proposed.settingsVersion !== 'number') return null;
  const hash = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return approveMemo(proposed.settingsVersion, signatureHex(new Uint8Array(hash)));
}

// Whether two proposals show the same settings, field by field as the summary shows them
export function sameSummary(a: ProposedSettings | null | undefined, b: ProposedSettings | null | undefined): boolean {
  return canonicalJson(approveSummaryRows(a)) === canonicalJson(approveSummaryRows(b));
}
